import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  MapPin,
  Loader2,
  Globe,
  CalendarDays,
  Navigation,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import SiteNav from "../components/Layout/SiteNav";
import { savePublicCalendarContext } from "@/lib/publicCalendarContext";
import { isSafeExternalUrl } from "@/lib/utils";
import { getNearbyMasjids } from "../services/api";
import useHalalFeature from "../hooks/useHalalFeature";
import { Input } from "@/components/ui/input";
import {
  BRAND,
  EDITORIAL_FONT,
  PatternTile,
  Eyebrow,
  EditorialShell,
  EditorialCard,
  SerifHeading,
  PillButton,
  useEditorialPalette,
} from "../components/editorial/EditorialPrimitives";

// ────────────────────────────────────────────────────────────────────────────────
// Utility Functions
// ────────────────────────────────────────────────────────────────────────────────

// Format a "HH:MM" time with AM/PM using prayer-specific rules.
const formatTimeWithAMPM = (time, prayerName = null) => {
  if (!time) return "-";

  // If already has AM/PM, return as is
  if (/[AaPp][Mm]/.test(time)) return time;

  // Parse time (expecting HH:MM format)
  const [hours, minutes] = time.split(":").map(Number);
  if (isNaN(hours) || isNaN(minutes)) return time;

  let period;
  let displayHours = hours;

  // Apply prayer-specific rules (similar to teleview images logic)
  if (prayerName === "fajr" || prayerName === "sunrise") {
    // Fajr and Sunrise are always AM
    period = "AM";
    displayHours = hours % 12 || 12;
  } else if (prayerName === "duhr" || prayerName === "jummah") {
    // Duhr/Jummah: only 10:00–11:59 can be AM; everything else PM
    if (hours >= 10 && hours < 12) {
      period = "AM";
      displayHours = hours;
    } else {
      period = "PM";
      displayHours = hours > 12 ? hours - 12 : hours;
    }
  } else if (prayerName === "asr" || prayerName === "maghrib" || prayerName === "isha") {
    // Asr, Maghrib, and Isha are always PM
    period = "PM";
    displayHours = hours > 12 ? hours - 12 : hours;
  } else {
    // Default fallback for times without prayer context
    period = hours >= 12 ? "PM" : "AM";
    displayHours = hours % 12 || 12;
  }

  return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
};

// ────────────────────────────────────────────────────────────────────────────────
// Prayer times panel (shown when a masjid card is expanded)
// ────────────────────────────────────────────────────────────────────────────────

function PrayerTimesPanel({ masjid, palette }) {
  const prayers = [
    { name: "Fajr", adhan: masjid.fajr_adhan_time, iqama: masjid.fajr_iqama_time },
    // Sunrise has a single time; show it right after Fajr.
    ...(masjid.sunrise_time
      ? [{ name: "Sunrise", time: masjid.sunrise_time, single: true }]
      : []),
    { name: "Dhuhr", adhan: masjid.duhr_adhan_time, iqama: masjid.duhr_iqama_time },
    { name: "Asr", adhan: masjid.asr_adhan_time, iqama: masjid.asr_iqama_time },
    { name: "Maghrib", adhan: masjid.maghrib_adhan_time, iqama: masjid.maghrib_iqama_time },
    { name: "Isha", adhan: masjid.isha_adhan_time, iqama: masjid.isha_iqama_time },
  ];

  // Pair each Jummah slot with its khutbah time (matches the app's MasjidDetail
  // design: a Jummah / Khutbah / Prayer table rather than separate lists).
  const jummahSlots = ["1", "2", "3"].filter(
    (slot) => masjid[`jummah_time_${slot}`] || masjid[`khutbah_time_${slot}`]
  );
  const hasMultipleJummah = jummahSlots.length > 1;
  const romanBySlot = { 1: "I", 2: "II", 3: "III" };

  // Both tables share the same column template so they line up vertically.
  const gridCols = "grid grid-cols-[2fr_1fr_1fr]";
  const headerCell = {
    fontFamily: EDITORIAL_FONT.sans,
    fontWeight: 600,
    letterSpacing: "0.14em",
    color: palette.muted,
  };
  const rowStyle = {
    fontFamily: EDITORIAL_FONT.sans,
    color: palette.ink2,
  };

  return (
    <div
      className="overflow-hidden rounded-xl"
      style={{ border: `1px solid ${palette.hairline}` }}
    >
      {/* Daily prayers — column headers */}
      <div
        className={`${gridCols} px-4 py-2.5 text-[10px] uppercase`}
        style={{
          background: palette.ivory,
          borderBottom: `1px solid ${palette.hairline}`,
          ...headerCell,
        }}
      >
        <span>Prayer</span>
        <span className="text-right">Adhan</span>
        <span className="text-right">Iqamah</span>
      </div>

      {/* Daily prayer rows */}
      {prayers.map((prayer, i) => (
        <div
          key={prayer.name}
          className={`${gridCols} items-center px-4 py-2.5 text-sm`}
          style={{
            borderBottom:
              i < prayers.length - 1 ? `1px solid ${palette.hairline}` : "none",
            ...rowStyle,
          }}
        >
          <span style={{ color: palette.ink, fontWeight: 600 }}>{prayer.name}</span>
          {prayer.single ? (
            <span className="col-span-2 text-right">
              {formatTimeWithAMPM(prayer.time, prayer.name.toLowerCase())}
            </span>
          ) : (
            <>
              <span className="text-right">
                {formatTimeWithAMPM(prayer.adhan, prayer.name.toLowerCase())}
              </span>
              <span className="text-right">
                {formatTimeWithAMPM(prayer.iqama, prayer.name.toLowerCase())}
              </span>
            </>
          )}
        </div>
      ))}

      {/* Jummah & Khutbah table */}
      {jummahSlots.length > 0 && (
        <>
          <div
            className={`${gridCols} px-4 py-2.5 text-[10px] uppercase`}
            style={{
              background: palette.ivory,
              borderTop: `1px solid ${palette.hairline}`,
              borderBottom: `1px solid ${palette.hairline}`,
              ...headerCell,
            }}
          >
            <span />
            <span className="text-right">Khutbah</span>
            <span className="text-right">Prayer</span>
          </div>

          {jummahSlots.map((slot, i) => {
            const prayerTime = masjid[`jummah_time_${slot}`];
            const khutbahTime = masjid[`khutbah_time_${slot}`];
            const label = hasMultipleJummah
              ? `Jummah ${romanBySlot[slot]}`
              : "Jummah";

            return (
              <div
                key={slot}
                className={`${gridCols} items-center px-4 py-2.5 text-sm`}
                style={{
                  borderBottom:
                    i < jummahSlots.length - 1
                      ? `1px solid ${palette.hairline}`
                      : "none",
                  ...rowStyle,
                }}
              >
                <span style={{ color: palette.ink, fontWeight: 600 }}>{label}</span>
                <span className="text-right">
                  {khutbahTime ? formatTimeWithAMPM(khutbahTime, "jummah") : "—"}
                </span>
                <span className="text-right">
                  {prayerTime ? formatTimeWithAMPM(prayerTime, "jummah") : "—"}
                </span>
              </div>
            );
          })}
        </>
      )}

    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────────
// Masjid card
// ────────────────────────────────────────────────────────────────────────────────

function MasjidCard({ masjid, onOpenCalendar }) {
  const palette = useEditorialPalette();
  const [expanded, setExpanded] = useState(false);
  const calendarId = masjid.masjidId || masjid.id || masjid.key;

  return (
    <EditorialCard
      variant="paper"
      className="group overflow-hidden p-7 sm:p-8 transition-all duration-300 hover:-translate-y-0.5"
      style={{
        transition:
          "box-shadow 300ms ease, transform 300ms ease, border-color 300ms ease",
      }}
    >
      <div className="flex items-start">
        <div className="min-w-0 flex-1">
          {masjid.distance != null && (
            <div
              className="mb-2 inline-flex items-center gap-1.5 text-[11px] uppercase"
              style={{
                fontFamily: EDITORIAL_FONT.sans,
                letterSpacing: "0.16em",
                color: palette.muted,
                fontWeight: 600,
              }}
            >
              <MapPin className="h-3 w-3" style={{ color: BRAND.gold }} />
              {masjid.distance.toFixed(1)} mi away
            </div>
          )}

          <SerifHeading
            level={3}
            className="mb-3"
            style={{
              fontSize: 24,
              fontWeight: 500,
              lineHeight: 1.1,
              letterSpacing: "-0.02em",
            }}
          >
            {masjid.title}
          </SerifHeading>

          <div
            className="flex items-start gap-2.5 text-sm"
            style={{ color: palette.ink2, fontFamily: EDITORIAL_FONT.sans }}
          >
            <MapPin
              className="mt-0.5 h-4 w-4 flex-shrink-0"
              style={{ color: BRAND.gold }}
            />
            <span>
              {masjid.address}
              {(masjid.city || masjid.state || masjid.zip) && (
                <>
                  <br />
                  {masjid.city}, {masjid.state} {masjid.zip}
                </>
              )}
            </span>
          </div>

          {/* Actions */}
          <div className="mt-5 flex flex-wrap gap-2.5">
            {isSafeExternalUrl(masjid.website) && (
              <PillButton variant="ghost" size="sm" asChild>
                <a
                  href={isSafeExternalUrl(masjid.website)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Globe className="h-4 w-4" />
                  Website
                </a>
              </PillButton>
            )}

            <PillButton
              variant={expanded ? "primary" : "outline"}
              size="sm"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? (
                <>
                  Hide times <ChevronUp className="h-4 w-4" />
                </>
              ) : (
                <>
                  Prayer times <ChevronDown className="h-4 w-4" />
                </>
              )}
            </PillButton>

            {expanded && calendarId && (
              <PillButton
                variant="ghost"
                size="sm"
                onClick={() => onOpenCalendar?.(masjid)}
              >
                <CalendarDays className="h-4 w-4" />
                Monthly calendar
              </PillButton>
            )}
          </div>

          {/* Animated expand/collapse: the grid-rows 0fr→1fr trick smoothly
              transitions height in both directions without measuring it. */}
          <div
            className="grid transition-[grid-template-rows] duration-300 ease-in-out"
            style={{ gridTemplateRows: expanded ? "1fr" : "0fr" }}
          >
            <div
              className={`overflow-hidden transition-opacity duration-300 ${
                expanded ? "opacity-100" : "opacity-0"
              }`}
            >
              <div className="pt-5">
                <PrayerTimesPanel masjid={masjid} palette={palette} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </EditorialCard>
  );
}

// ────────────────────────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────────────────────────

export default function PublicMasjids() {
  const navigate = useNavigate();
  const palette = useEditorialPalette();
  const { halalEnabled } = useHalalFeature();
  const [masjids, setMasjids] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [location, setLocation] = useState(null);
  const [locationName, setLocationName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [locationSearch, setLocationSearch] = useState("");
  const [searchingLocation, setSearchingLocation] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Geocode a location string to lat/lon using OpenStreetMap Nominatim
  const geocodeLocation = async (query) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&countrycodes=us&limit=1`,
        {
          headers: {
            "User-Agent": "MuslimPlusApp/1.0",
          },
        }
      );
      const data = await response.json();
      if (data && data.length > 0) {
        return {
          latitude: parseFloat(data[0].lat),
          longitude: parseFloat(data[0].lon),
          displayName: data[0].display_name.split(",").slice(0, 3).join(","),
        };
      }
      return null;
    } catch (err) {
      console.error("Geocoding error:", err);
      return null;
    }
  };

  // Search for masjids in a different location
  const searchLocation = async () => {
    if (!locationSearch.trim()) return;

    setSearchingLocation(true);
    setError(null);

    try {
      const result = await geocodeLocation(locationSearch);
      if (result) {
        setLocation({ latitude: result.latitude, longitude: result.longitude });
        setLocationName(result.displayName);
        await fetchMasjids(result.latitude, result.longitude);
      } else {
        setError("Location not found. Try a city name, state, or zip code.");
      }
    } catch (err) {
      setError("Failed to search location. Please try again.");
    } finally {
      setSearchingLocation(false);
    }
  };

  // Fetch nearby masjids
  const fetchMasjids = async (lat, lon) => {
    setLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      const data = await getNearbyMasjids(lat, lon);
      if (data?.masjids) {
        setMasjids(data.masjids);
      } else {
        setMasjids([]);
      }
    } catch (err) {
      console.error("Error fetching masjids:", err);
      setError("Failed to load masajid. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Get user location
  const getUserLocation = async () => {
    setLoading(true);
    setError(null);

    try {
      if (!navigator.geolocation) {
        throw new Error("Geolocation is not supported by your browser");
      }

      // The Geolocation API only works on a secure origin (https or
      // localhost). On an insecure origin the browser rejects immediately
      // without ever showing a permission prompt, which looks like a silent
      // failure — so surface a clear message instead.
      if (window.isSecureContext === false) {
        setError(
          "Location needs a secure connection (https or localhost). Open this site over https, or search a city, state, or zip instead."
        );
        setLoading(false);
        return;
      }

      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000, // 5 minutes
        });
      });

      const { latitude, longitude } = position.coords;
      setLocation({ latitude, longitude });
      setLocationName("Current Location");
      setLocationSearch("");
      await fetchMasjids(latitude, longitude);
    } catch (err) {
      console.error("Error getting location:", err);

      // GeolocationPositionError codes: 1 = permission denied (no prompt means
      // it's blocked for this site), 2 = position unavailable, 3 = timeout.
      let message =
        "Unable to get your location. Please try searching a city, state, or zip instead.";
      if (err.code === 1) {
        message =
          "Location is blocked for this site. Click the location/lock icon in your browser's address bar to allow it, then try again — or search a city, state, or zip instead.";
      } else if (err.code === 3) {
        message =
          "Getting your location timed out. Please try again, or search a city, state, or zip.";
      }
      setError(message);
      setLoading(false);
    }
  };

  // We never auto-locate on mount; the user opts in via "Use my location"
  // or by searching a location.

  // Filter loaded masjids by the name/city/zip query (instant, client-side)
  const filteredMasjids = masjids.filter((masjid) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      masjid.title?.toLowerCase().includes(query) ||
      masjid.city?.toLowerCase().includes(query) ||
      masjid.state?.toLowerCase().includes(query) ||
      masjid.zip?.includes(query)
    );
  });

  // Shared field styling for inputs inside the editorial cards
  const fieldStyle = {
    background: palette.ivory,
    border: `1px solid ${palette.hairline}`,
    fontFamily: EDITORIAL_FONT.sans,
    color: palette.ink,
  };
  const labelStyle = {
    fontFamily: EDITORIAL_FONT.sans,
    fontWeight: 600,
    letterSpacing: "0.16em",
    color: palette.muted,
  };

  return (
    <EditorialShell>
      <SiteNav />

      {/* ══════════════════════════════════
          HERO
          ══════════════════════════════════ */}
      <section
        className="relative overflow-hidden"
        style={{ background: palette.ivory }}
      >
        <PatternTile opacity={0.04} />
        <div
          className="relative mx-auto px-4 pt-16 pb-12 sm:px-6 sm:pt-20 sm:pb-16 lg:px-10 lg:pt-24 lg:pb-20"
          style={{ maxWidth: 1320 }}
        >
          <div className="grid gap-10 lg:grid-cols-[1.4fr_1fr] lg:gap-16 lg:items-end">
            <div>
              <Eyebrow>Community masajid</Eyebrow>
              <SerifHeading
                level={1}
                className="mt-6 mb-6"
                style={{
                  fontSize: "clamp(2.5rem, 6.5vw, 5.5rem)",
                  lineHeight: 0.96,
                  letterSpacing: "-0.035em",
                }}
              >
                Find the masajid
                <br />
                around you, with
                <br />
                <em style={{ color: BRAND.gold, fontWeight: 400 }}>
                  live prayer times.
                </em>
              </SerifHeading>
              <p
                className="max-w-xl text-base sm:text-lg"
                style={{
                  color: palette.ink2,
                  lineHeight: 1.55,
                  fontFamily: EDITORIAL_FONT.sans,
                }}
              >
                Discover masajid near you with accurate adhan and iqamah times,
                Jummah schedules, and monthly calendars — synced directly from each
                masjid.
              </p>
            </div>

            {/* Search / filter panel */}
            <div>
              <EditorialCard
                variant="paper"
                className="p-6 sm:p-7"
                style={{ borderLeft: `3px solid ${BRAND.gold}`, borderRadius: 18 }}
              >
                <div
                  className="mb-3 text-[11px] uppercase"
                  style={labelStyle}
                >
                  Search by location
                </div>

                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <MapPin
                      className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2"
                      style={{ color: palette.muted }}
                    />
                    <Input
                      placeholder="City, state, or zip"
                      value={locationSearch}
                      onChange={(e) => setLocationSearch(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && searchLocation()}
                      className="h-12 rounded-full pl-11 text-sm"
                      style={fieldStyle}
                    />
                  </div>
                  <PillButton
                    variant="gold"
                    onClick={searchLocation}
                    disabled={searchingLocation || !locationSearch.trim()}
                    style={{ height: 48, paddingLeft: 18, paddingRight: 18 }}
                    aria-label="Search location"
                  >
                    {searchingLocation ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Search className="h-4 w-4" />
                    )}
                  </PillButton>
                </div>

                <div className="mt-3">
                  <PillButton
                    variant="outline"
                    onClick={getUserLocation}
                    disabled={loading}
                    className="w-full justify-center"
                    style={{ height: 44 }}
                  >
                    <Navigation className="h-4 w-4" />
                    Use my location
                  </PillButton>
                </div>

                {locationName && (
                  <div
                    className="mt-3 flex items-center gap-2 rounded-full px-3.5 py-2 text-[12px]"
                    style={{
                      background: `${BRAND.gold}14`,
                      border: `1px solid ${palette.hairline}`,
                      fontFamily: EDITORIAL_FONT.sans,
                      color: palette.ink2,
                    }}
                  >
                    <MapPin
                      className="h-3.5 w-3.5 flex-shrink-0"
                      style={{ color: BRAND.gold }}
                    />
                    <span className="truncate">
                      <span style={{ color: palette.muted }}>Near </span>
                      {locationName}
                    </span>
                  </div>
                )}
              </EditorialCard>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
          MASAJID LISTING
          ══════════════════════════════════ */}
      <section style={{ background: palette.paper }}>
        <div
          className="mx-auto px-4 py-16 sm:px-6 sm:py-20 lg:px-10 lg:py-24"
          style={{ maxWidth: 1320 }}
        >
          {error && (
            <div
              className="mb-8 rounded-2xl px-5 py-4 text-sm"
              style={{
                background: "transparent",
                border: `1px solid ${BRAND.terracotta}`,
                color: BRAND.terracotta,
                fontFamily: EDITORIAL_FONT.sans,
              }}
            >
              {error}
            </div>
          )}

          {loading && (
            <div
              className="flex flex-col items-center justify-center py-24"
              style={{ color: palette.muted, fontFamily: EDITORIAL_FONT.sans }}
            >
              <Loader2
                className="mb-4 h-7 w-7 animate-spin"
                style={{ color: BRAND.gold }}
              />
              <span className="text-sm" style={{ letterSpacing: "0.04em" }}>
                Finding masajid near you…
              </span>
            </div>
          )}

          {!loading && masjids.length > 0 && (
            <div
              className="mb-10 flex flex-col gap-6 md:flex-row md:items-end md:justify-between"
              style={{
                borderBottom: `1px solid ${palette.hairline}`,
                paddingBottom: 24,
              }}
            >
              <div>
                <Eyebrow>What's nearby</Eyebrow>
                <SerifHeading
                  className="mt-4"
                  style={{
                    fontSize: "clamp(1.75rem, 3.5vw, 2.75rem)",
                    letterSpacing: "-0.03em",
                  }}
                >
                  {searchQuery ? (
                    <>
                      <em style={{ color: BRAND.gold, fontWeight: 400 }}>
                        {filteredMasjids.length}
                      </em>{" "}
                      matching "{searchQuery}"
                    </>
                  ) : (
                    <>
                      <em style={{ color: BRAND.gold, fontWeight: 400 }}>
                        {filteredMasjids.length}
                      </em>{" "}
                      masajid nearby
                    </>
                  )}
                </SerifHeading>
              </div>

              {/* Filter loaded results by name */}
              <div className="relative w-full md:max-w-xs">
                <Search
                  className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2"
                  style={{ color: palette.muted }}
                />
                <Input
                  placeholder="Filter by name, city, or zip"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-11 rounded-full pl-11 text-sm"
                  style={fieldStyle}
                />
              </div>
            </div>
          )}

          {!loading && filteredMasjids.length > 0 ? (
            <div className="grid gap-6 md:grid-cols-2 md:items-start">
              {filteredMasjids.map((masjid) => (
                <MasjidCard
                  key={masjid.masjidId || masjid.key}
                  masjid={masjid}
                  onOpenCalendar={(m) => {
                    const id = m.masjidId || m.id || m.key;
                    if (!id) return;
                    savePublicCalendarContext({ masjidId: id, masjid: m });
                    navigate("/masjids/calendar", { state: { masjid: m } });
                  }}
                />
              ))}
            </div>
          ) : (
            !loading &&
            !error && (
              <EditorialCard
                variant="ivory"
                className="px-8 py-20 text-center sm:py-24"
                style={{ borderRadius: 22 }}
              >
                <SerifHeading
                  className="mb-4"
                  style={{
                    fontSize: "clamp(1.75rem, 4vw, 2.5rem)",
                    letterSpacing: "-0.03em",
                  }}
                >
                  {!hasSearched ? (
                    <>
                      Find masajid{" "}
                      <em style={{ color: BRAND.gold, fontWeight: 400 }}>near you.</em>
                    </>
                  ) : searchQuery ? (
                    <>
                      No masajid{" "}
                      <em style={{ color: BRAND.gold, fontWeight: 400 }}>found.</em>
                    </>
                  ) : (
                    <>
                      No masajid{" "}
                      <em style={{ color: BRAND.gold, fontWeight: 400 }}>nearby.</em>
                    </>
                  )}
                </SerifHeading>
                <p
                  className="mx-auto max-w-md text-base"
                  style={{
                    color: palette.ink2,
                    fontFamily: EDITORIAL_FONT.sans,
                    lineHeight: 1.6,
                  }}
                >
                  {!hasSearched
                    ? "Search a city, state, or zip code — or use your location to find masajid around you."
                    : searchQuery
                    ? "Try a different name, city, or zip code."
                    : "Try a different location or check back later."}
                </p>
              </EditorialCard>
            )
          )}
        </div>
      </section>

      {/* ══════════════════════════════════
          CTA — REGISTER YOUR MASJID
          ══════════════════════════════════ */}
      {!loading && masjids.length > 0 && (
        <section
          className="relative overflow-hidden"
          style={{
            background: palette.ivory,
            borderTop: `1px solid ${palette.hairline}`,
          }}
        >
          <div
            className="relative mx-auto grid gap-12 px-4 py-20 sm:px-6 sm:py-24 lg:grid-cols-2 lg:items-center lg:gap-16 lg:px-10"
            style={{ maxWidth: 1320 }}
          >
            <div>
              <Eyebrow>For masajid</Eyebrow>
              <SerifHeading
                className="mt-5 mb-7"
                style={{
                  fontSize: "clamp(2rem, 5vw, 4rem)",
                  lineHeight: 1,
                  letterSpacing: "-0.03em",
                }}
              >
                Is your masjid
                <br />
                listed?{" "}
                <em style={{ color: BRAND.gold, fontWeight: 400 }}>Add it.</em>
              </SerifHeading>
              <p
                className="mb-8 max-w-xl text-base sm:text-lg"
                style={{
                  color: palette.ink2,
                  fontFamily: EDITORIAL_FONT.sans,
                  lineHeight: 1.55,
                }}
              >
                Register your masjid to manage prayer times, publish events, and keep
                your community informed — free forever.
              </p>
              <PillButton variant="primary" onClick={() => navigate("/signup")}>
                Register your masjid
                <span aria-hidden>→</span>
              </PillButton>
            </div>

            <EditorialCard
              variant="paper"
              className="p-8 sm:p-10"
              style={{ borderLeft: `3px solid ${BRAND.gold}`, borderRadius: 20 }}
            >
              <div
                className="mb-4 text-xs uppercase"
                style={labelStyle}
              >
                Why masajid use Muslim+
              </div>
              <ul
                className="flex flex-col gap-4 text-[15px]"
                style={{
                  color: palette.ink2,
                  fontFamily: EDITORIAL_FONT.sans,
                  lineHeight: 1.55,
                }}
              >
                {[
                  {
                    t: "Accurate prayer times.",
                    b: "Publish adhan and iqamah updates instantly across app, TV, and web.",
                  },
                  {
                    t: "Reach the ummah locally.",
                    b: "Your masjid surfaces to Muslims searching nearby.",
                  },
                  {
                    t: "No ads, no fees.",
                    b: "A free, ad-free home for your community calendar.",
                  },
                ].map((item) => (
                  <li key={item.t} className="flex items-start gap-3">
                    <span
                      aria-hidden
                      className="mt-2 flex-shrink-0"
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: 999,
                        background: BRAND.gold,
                      }}
                    />
                    <span>
                      <strong style={{ color: palette.ink, fontWeight: 600 }}>
                        {item.t}
                      </strong>{" "}
                      {item.b}
                    </span>
                  </li>
                ))}
              </ul>
            </EditorialCard>
          </div>
        </section>
      )}

      {/* ══════════════════════════════════
          FOOTER
          ══════════════════════════════════ */}
      <footer
        className="relative"
        style={{
          background: palette.paper,
          borderTop: `1px solid ${palette.hairline}`,
          color: palette.ink,
        }}
      >
        <div className="container mx-auto px-4 py-16 sm:px-6 lg:px-10">
          <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
            <div className="lg:col-span-2">
              <div className="mb-4 flex items-center gap-2.5">
                <span
                  style={{
                    fontFamily: EDITORIAL_FONT.serif,
                    fontSize: 24,
                    fontWeight: 600,
                    letterSpacing: "-0.02em",
                  }}
                >
                  Muslim<span style={{ color: BRAND.gold }}>+</span>
                </span>
              </div>
              <p
                className="mb-6 max-w-md text-[15px] leading-relaxed"
                style={{ color: palette.muted }}
              >
                Connecting Muslims with their community through technology and faith.
                Find prayer times, discover nearby masajid, stay updated with events
                {halalEnabled ? ", and explore halal restaurants." : "."}
              </p>
              <div className="flex flex-wrap gap-2">
                {["Trusted Platform", "Verified Content", "Real-time Updates"].map(
                  (tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center rounded-full px-3 py-1 text-[11px] font-medium"
                      style={{
                        background: "transparent",
                        border: `1px solid ${palette.hairline}`,
                        color: palette.muted,
                        letterSpacing: "0.04em",
                      }}
                    >
                      {tag}
                    </span>
                  )
                )}
              </div>
            </div>

            <div>
              <div
                className="mb-5 text-[11px] font-semibold uppercase"
                style={{ letterSpacing: "0.16em", color: palette.muted }}
              >
                Quick links
              </div>
              <div className="flex flex-col gap-3 text-sm">
                <button
                  onClick={() => navigate("/masjids")}
                  className="text-left transition-colors hover:text-[#bf9a2f]"
                >
                  Find Masajid
                </button>
                <button
                  onClick={() => navigate("/events-public")}
                  className="text-left transition-colors hover:text-[#bf9a2f]"
                >
                  Browse Events
                </button>
                <button
                  onClick={() => navigate("/login")}
                  className="text-left transition-colors hover:text-[#bf9a2f]"
                >
                  Sign In
                </button>
                <button
                  onClick={() => navigate("/policy")}
                  className="text-left transition-colors hover:text-[#bf9a2f]"
                >
                  Privacy Policy
                </button>
              </div>
            </div>

            <div>
              <div
                className="mb-5 text-[11px] font-semibold uppercase"
                style={{ letterSpacing: "0.16em", color: palette.muted }}
              >
                Resources
              </div>
              <div className="flex flex-col gap-3 text-sm">
                <a
                  href="https://apps.apple.com/us/app/muslim-prayer-companion/id6745054019"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-colors hover:text-[#bf9a2f]"
                >
                  Download iOS App
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.psu.pdxmsa&pcampaignid=web_share"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-colors hover:text-[#bf9a2f]"
                >
                  Download Android App
                </a>
                <a
                  href="https://www.instagram.com/muslimplus.app/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-colors hover:text-[#bf9a2f]"
                >
                  Follow us on Instagram
                </a>
              </div>
            </div>
          </div>

          <div
            className="mt-12 flex flex-col items-start justify-between gap-3 border-t pt-8 text-xs md:flex-row md:items-center"
            style={{ borderColor: palette.hairline, color: palette.muted }}
          >
            <span>© {new Date().getFullYear()} Muslim+. All rights reserved.</span>
            <span>Made with ❤️ for the Muslim community</span>
          </div>
        </div>
      </footer>
    </EditorialShell>
  );
}
